'use client';

import React, { useState } from 'react';
import { XMarkIcon, FolderIcon } from '@heroicons/react/24/outline';

interface CreateFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateFolder: (folderName: string) => void;
}

export default function CreateFolderModal({ isOpen, onClose, onCreateFolder }: CreateFolderModalProps) {
    const [folderName, setFolderName] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const validateFolderName = (name: string): string => {
        if (!name.trim()) return 'Tên folder không được để trống';
        if (name.includes('/')) return 'Tên folder không được chứa ký tự /';
        if (name.includes('\\')) return 'Tên folder không được chứa ký tự \\';
        if (name.match(/[<>:"|?*]/)) return 'Tên folder chứa ký tự không hợp lệ';
        if (name.length > 255) return 'Tên folder quá dài (tối đa 255 ký tự)';
        return '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const validationError = validateFolderName(folderName);
        if (validationError) {
            setError(validationError);
            return;
        }

        setCreating(true);
        setError('');
        
        try {
            await onCreateFolder(folderName.trim());
            setFolderName('');
            onClose();
        } catch (error) {
            setError('Có lỗi xảy ra khi tạo folder');
            console.error('Create folder error:', error);
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        if (!creating) {
            setFolderName('');
            setError('');
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FolderIcon className="w-5 h-5 text-blue-600" />
                        Tạo Folder Mới
                    </h3>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={creating}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit}>
                    <div className="p-4">
                        <div className="mb-4">
                            <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-2">
                                Tên folder
                            </label>
                            <input
                                type="text"
                                id="folderName"
                                value={folderName}
                                onChange={(e) => {
                                    setFolderName(e.target.value);
                                    setError('');
                                }}
                                placeholder="Nhập tên folder..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                disabled={creating}
                                autoFocus
                            />
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        <div className="text-xs text-gray-500">
                            <p>Lưu ý:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                                <li>Tên folder không được chứa các ký tự: / \ &lt; &gt; : " | ? *</li>
                                <li>Độ dài tối đa 255 ký tự</li>
                            </ul>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            disabled={creating}
                        >
                            Hủy
                        </button>
                        <button
                            type="submit"
                            disabled={!folderName.trim() || creating}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {creating && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            {creating ? 'Đang tạo...' : 'Tạo Folder'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
